import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../Context/AuthContext";
import { useNavigate } from "react-router-dom";

import DoctorPortalLayout from "../../components/Doctor/DoctorPortalLayout";
import DoctorSchedule from "../../components/Doctor/DoctorSchedule";
import SlotManager from "../../components/Doctor/SlotManager";
import DoctorMessages from "../../components/Doctor/DoctorMessages";
import BlogManager from "../../components/Doctor/BlogManager";
import DoctorProfile from "../../components/Doctor/DoctorProfile";
import ShopManager from "../../components/Doctor/ShopManager";

const DoctorDashboard = () => {
  const [activePage, setActivePage] = useState(() => {
    const path = window.location.pathname;
    if (path.includes("slots")) return "slots";
    if (path.includes("messages")) return "messages";
    if (path.includes("blogs")) return "blogs";
    if (path.includes("profile")) return "profile";
    if (path.includes("shop")) return "shop";
    return "schedule";
  });
  const [doctor, setDoctor] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [chatPatient, setChatPatient] = useState(null);

  // Slots state
  const [slots, setSlots] = useState([]);
  const [slotDate, setSlotDate] = useState(() => {
    return new Date().toISOString().substring(0, 10);
  });
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [editedSlotTimes, setEditedSlotTimes] = useState({
    startTime: "",
    endTime: "",
  });

  // Blogs state
  const [blogs, setBlogs] = useState([]);
  const [newBlogData, setNewBlogData] = useState({
    headTitle: "",
    body: "",
    image: "",
  });
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [editingBlogData, setEditingBlogData] = useState({
    headTitle: "",
    body: "",
    image: "",
  });

  // Appointments editing status
  const [editedStatus, setEditedStatus] = useState({});

  const token = localStorage.getItem("token");
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const baseUrl = "/api";

  // Fetch doctor profile
  useEffect(() => {
    const doctorId = localStorage.getItem("userId");
    if (doctorId && token) {
      axios
        .get(`${baseUrl}/doctor/${doctorId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setDoctor(res.data.doctor))
        .catch((err) => console.error("Doctor fetch error:", err));
    }
  }, [token]);

  // Fetch appointments
  useEffect(() => {
    const doctorId = localStorage.getItem("userId");
    if (doctorId && token) {
      axios
        .get(`${baseUrl}/appointment/doctor/${doctorId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setAppointments(res.data.appointment || []))
        .catch((err) => console.error("Appointments fetch error:", err));
    }
  }, [token]);

  // Fetch slots for selected date
  useEffect(() => {
    const doctorId = localStorage.getItem("userId");
    if (doctorId && slotDate && token) {
      axios
        .get(
          `${baseUrl}/appointment/doctor/slots?doctorId=${doctorId}&date=${slotDate}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .then((res) =>
          setSlots(
            (res.data?.slots || []).sort((a, b) => a.startTime.localeCompare(b.startTime))
          )
        )
        .catch(() => setSlots([]));
    }
  }, [token, slotDate, editingSlotId]);

  // Fetch blogs
  const fetchBlogs = async () => {
    const doctorId = localStorage.getItem("userId");
    if (!doctorId || !token) return;
    try {
      const response = await axios.get(`${baseUrl}/doctor/${doctorId}/blogs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sortedBlogs = (response.data.blogs || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setBlogs(sortedBlogs);
    } catch (error) {
      console.error("Blogs fetch error:", error);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, [token]);

  // ─── APPOINTMENTS API CALLBACKS ───────────────────────────────────
  const handleStatusChange = (event, appointment) => {
    setEditedStatus((prev) => ({ ...prev, [appointment._id]: event.target.value }));
  };

  const saveEditedStatus = async (appointmentId) => {
    const newStatus = editedStatus[appointmentId];
    try {
      const response = await axios.patch(
        `${baseUrl}/appointment/${appointmentId}`,
        { status: newStatus, role: "doctor" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        setAppointments(
          appointments.map((ap) =>
            ap._id === appointmentId ? { ...ap, status: newStatus } : ap
          )
        );
        toast.success("Appointment status updated successfully");
      } else {
        toast.error("Failed to update appointment status");
      }
    } catch {
      toast.error("Error updating appointment status");
    }
  };

  const onOpenChat = (patient) => {
    setChatPatient(patient);
    setActivePage("messages");
  };

  // ─── SLOTS API CALLBACKS ──────────────────────────────────────────
  const generateSlots = async () => {
    if (!doctor || !slotDate) return;
    try {
      await axios.post(
        `${baseUrl}/appointment/doctor/${doctor._id}/generate-slots`,
        { date: slotDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Slots generated for the day!");
      const res = await axios.get(
        `${baseUrl}/appointment/doctor/slots?doctorId=${doctor._id}&date=${slotDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlots(res.data.slots || []);
    } catch {
      toast.error("Error generating slots for the day.");
    }
  };

  const toggleSlotAvailability = async (slotId, available) => {
    try {
      await axios.patch(
        `${baseUrl}/appointment/slot/${slotId}/availability`,
        { available },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlots((prev) =>
        prev.map((slot) =>
          slot._id === slotId ? { ...slot, isAvailable: available } : slot
        )
      );
      toast.success(`Slot marked as ${available ? "available" : "unavailable"}`);
    } catch {
      toast.error("Failed to update slot availability");
    }
  };

  const startEditSlot = (slotId, slot) => {
    setEditingSlotId(slotId);
    setEditedSlotTimes({ startTime: slot.startTime, endTime: slot.endTime });
  };

  const cancelEditSlot = () => {
    setEditingSlotId(null);
    setEditedSlotTimes({ startTime: "", endTime: "" });
  };

  const saveEditedSlot = async (slotId) => {
    if (!editedSlotTimes.startTime || !editedSlotTimes.endTime) {
      toast.error("Both times are required");
      return;
    }
    try {
      const response = await axios.patch(
        `${baseUrl}/appointment/slot/${slotId}/timings`,
        editedSlotTimes,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlots((prev) =>
        prev.map((slot) =>
          slot._id === slotId
            ? {
                ...slot,
                startTime: response.data.slot.startTime,
                endTime: response.data.slot.endTime,
              }
            : slot
        )
      );
      setEditingSlotId(null);
      toast.success("Slot timings updated");
    } catch {
      toast.error("Unable to update slot timings. Only unbooked slots can be edited.");
    }
  };

  // ─── BLOGS API CALLBACKS ──────────────────────────────────────────
  const handleBlogInputChange = (field, value) => {
    if (editingBlogId) {
      setEditingBlogData((prev) => ({ ...prev, [field]: value }));
    } else {
      setNewBlogData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const submitNewBlog = async () => {
    const doctorId = localStorage.getItem("userId");
    if (!newBlogData.headTitle || !newBlogData.body) {
      toast.error("Title and body are required for blog");
      return;
    }
    try {
      await axios.post(`${baseUrl}/doctor/${doctorId}/blogs`, newBlogData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Blog posted successfully");
      setNewBlogData({ headTitle: "", body: "", image: "" });
      fetchBlogs();
    } catch {
      toast.error("Failed to post blog");
    }
  };

  const startEditBlog = (blog) => {
    setEditingBlogId(blog._id);
    setEditingBlogData({
      headTitle: blog.headTitle,
      body: blog.body,
      image: blog.image || "",
    });
  };

  const cancelEditBlog = () => {
    setEditingBlogId(null);
    setEditingBlogData({ headTitle: "", body: "", image: "" });
  };

  const submitEditBlog = async () => {
    const doctorId = localStorage.getItem("userId");
    if (!editingBlogData.headTitle || !editingBlogData.body) {
      toast.error("Title and body are required for blog");
      return;
    }
    try {
      await axios.patch(
        `${baseUrl}/doctor/${doctorId}/blogs/${editingBlogId}`,
        editingBlogData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Blog updated successfully");
      setEditingBlogId(null);
      setEditingBlogData({ headTitle: "", body: "", image: "" });
      fetchBlogs();
    } catch {
      toast.error("Failed to update blog");
    }
  };

  const deleteBlog = async (blogId) => {
    const doctorId = localStorage.getItem("userId");
    try {
      await axios.delete(`${baseUrl}/doctor/${doctorId}/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Blog deleted successfully");
      fetchBlogs();
    } catch {
      toast.error("Failed to delete blog");
    }
  };

  // ─── PROFILE API CALLBACKS ────────────────────────────────────────
  const handleSaveAllProfile = async (formData) => {
    const userId = localStorage.getItem("userId");
    try {
      // Patch each updated field or send batch patch
      const requestBody = { ...formData, role: "doctor" };
      const response = await axios.patch(`${baseUrl}/doctor/${userId}`, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setDoctor((prev) => ({ ...prev, ...formData }));
        toast.success("All profile settings updated successfully!");
      } else {
        toast.error("Failed to update profile settings");
      }
    } catch {
      toast.error("Error saving clinic settings");
    }
  };

  const docName = doctor ? `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim() || doctor.name || "Doctor" : "Doctor";

  return (
    <DoctorPortalLayout
      activePage={activePage}
      setActivePage={setActivePage}
      doctorName={docName}
      handleLogout={logout}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {activePage === "schedule" && (
        <DoctorSchedule
          appointments={appointments}
          handleStatusChange={handleStatusChange}
          saveEditedStatus={saveEditedStatus}
          editedStatus={editedStatus}
          onOpenChat={onOpenChat}
        />
      )}

      {activePage === "slots" && (
        <SlotManager
          slots={slots}
          slotDate={slotDate}
          setSlotDate={setSlotDate}
          generateSlots={generateSlots}
          toggleSlotAvailability={toggleSlotAvailability}
          editingSlotId={editingSlotId}
          startEditSlot={startEditSlot}
          cancelEditSlot={cancelEditSlot}
          editedSlotTimes={editedSlotTimes}
          setEditedSlotTimes={setEditedSlotTimes}
          saveEditedSlot={saveEditedSlot}
        />
      )}

      {activePage === "messages" && (
        <DoctorMessages
          doctor={doctor}
          initialPatient={chatPatient}
        />
      )}

      {activePage === "blogs" && (
        <BlogManager
          blogs={blogs}
          newBlogData={newBlogData}
          handleBlogInputChange={handleBlogInputChange}
          submitNewBlog={submitNewBlog}
          editingBlogId={editingBlogId}
          editingBlogData={editingBlogData}
          startEditBlog={startEditBlog}
          cancelEditBlog={cancelEditBlog}
          submitEditBlog={submitEditBlog}
          deleteBlog={deleteBlog}
        />
      )}

      {activePage === "profile" && (
        <DoctorProfile
          doctor={doctor}
          onSaveAll={handleSaveAllProfile}
        />
      )}

      {activePage === "shop" && <ShopManager />}
    </DoctorPortalLayout>
  );
};

export default DoctorDashboard;
